import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../Modal';
import { ClientModal } from '../ClientModal';
import { EstimatorModal } from '../EstimatorModal';
import { QuoteWorksheet } from '../QuoteWorksheet';
import { MaterialsList } from '../MaterialsList';
import { quotesService, clientsService, servicesService, settingsService, surfaceRatesService, jobsService, worksheetService } from '../../services/api';
import { formatMoney } from '../../utils/pricing';
import { formatApptDateLong, formatApptTime } from '../../utils/dateFormatters';
import { clearDraftsFor } from '../../utils/estimatorDraft';
import { buildQuoteLines, measurementsToShapes } from '../../utils/measure';
import './QuoteModal.css';

const MAP_LINE_OPTIONS = [
  { value: 'area', label: 'As priced lines — one per area' },
  { value: 'surface', label: 'As priced lines — one per surface' },
  { value: 'none', label: 'Listed under "Areas measured" only — no priced lines' }
];

// Move to costings (#773): which cost category a customer line most likely belongs under
const guessCategory = (description, cats) => {
  const named = (re) => cats.find((c) => re.test(c.name));
  if (/hire/i.test(description)) return named(/^equipment hire$/i);
  if (/labour|wages/i.test(description)) return named(/^wages/i) || cats.find((c) => c.kind === 'labour');
  return named(/^materials/i) || cats.find((c) => c.kind === 'material');
};

const STATUS_LABEL = { draft: 'Draft', sent: 'Sent', accepted: 'Accepted', declined: 'Declined', expired: 'Expired' };
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
let keySeq = 1;
const newItem = (patch = {}) => ({ key: keySeq++, service_id: '', description: '', qty: 1, unit_price: '', source: null, unit: null, ...patch });
const EMPTY = { customer_id: '', appointment_id: null, job_id: null, title: '', intro: '', terms: '', includes_gst: true, valid_until: '', items: [], measurements: [], show_areas: false, show_map: false, map_lines: 'area' };

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-CA', { timeZone: 'Australia/Brisbane' });
}
function totalsOf(items, includesGst) {
  const total = round2(items.reduce((s, it) => s + round2((Number(it.qty) || 0) * (Number(it.unit_price) || 0)), 0));
  const gst = includesGst ? round2(total / 11) : 0;
  return { total, gst, subtotal: round2(total - gst) };
}

/**
 * Thumbnail of the marked-up aerial view for a saved quote. Fetches the PNG
 * itself so a refusal from Google comes back as a readable reason (the Maps
 * Static API has to be enabled on the key) instead of a broken image.
 */
function MapPreview({ id, version }) {
  const [state, setState] = useState({ url: '', error: '' });
  useEffect(() => {
    let stale = false; let objectUrl = '';
    setState({ url: '', error: '' });
    (async () => {
      try {
        const res = await fetch(quotesService.mapUrl(id, version), { credentials: 'include' });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (!stale) setState({ url: '', error: data.error || `Map unavailable (HTTP ${res.status})` });
          return;
        }
        objectUrl = URL.createObjectURL(await res.blob());
        if (!stale) setState({ url: objectUrl, error: '' });
      } catch (e) {
        if (!stale) setState({ url: '', error: e.message || 'Map unavailable' });
      }
    })();
    return () => { stale = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id, version]);
  if (state.error) return <span className="form-error quote-map-error">Map image unavailable: {state.error}</span>;
  if (!state.url) return <span className="text-muted quote-hint">Fetching the map…</span>;
  return <img className="quote-map-preview" src={state.url} alt="Marked-up aerial view of the measured areas" />;
}

/**
 * Create / edit a quote. Pass `quote` (any row with an id) to edit — the full
 * record is fetched on open. Pass `prefill` for a new quote started from an
 * appointment or client. `onBook(quote)` shows a "Book job" action.
 */
export default function QuoteModal({ isOpen, onClose, onSave, onBook = null, quote = null, prefill = null }) {
  const [current, setCurrent] = useState(null);
  const [acceptUrl, setAcceptUrl] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [surfaces, setSurfaces] = useState([]);
  const [settings, setSettings] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [clientModal, setClientModal] = useState(null);
  const [jobOptions, setJobOptions] = useState([]); // jobs this quote could move to
  const [estimator, setEstimator] = useState(null); // null | 'edit' | 'view'
  const [tab, setTab] = useState('quote'); // quote | costings | materials (the last two need a saved quote)

  useEffect(() => {
    if (!isOpen) return;
    setErrors({}); setNotice(''); setBusy(''); setCurrent(null); setAcceptUrl(''); setEstimator(null); setTab('quote');
    let stale = false;
    (async () => {
      const [c, s, st, sr] = await Promise.all([clientsService.getAll(), servicesService.getAll(), settingsService.get(), surfaceRatesService.getAll()]);
      if (stale) return;
      const cfg = st.success ? (st.settings || {}) : {};
      setSettings(cfg);
      if (c.success) setClients(c.customers || []);
      if (s.success) setServices((s.services || []).filter((x) => x.is_active));
      if (sr.success) setSurfaces(sr.rates || []);
      if (quote?.id) {
        const r = await quotesService.getById(quote.id);
        if (stale) return;
        if (r.success) hydrate(r.quote, r.accept_url);
        else setErrors({ submit: r.error || 'Could not load quote' });
      } else {
        setForm({
          ...EMPTY,
          customer_id: prefill?.customer_id ? String(prefill.customer_id) : '',
          appointment_id: prefill?.appointment_id || null,
          job_id: prefill?.job_id || null,
          title: prefill?.title || '',
          terms: cfg.quote_terms || '',
          includes_gst: cfg.gst_registered !== 'false',
          valid_until: todayPlus(parseInt(cfg.quote_valid_days, 10) || 30),
          // Start with one blank line so the itemised breakdown is visible straight away.
          items: prefill?.items?.length ? prefill.items.map((it) => newItem(it)) : [newItem()],
          measurements: [],
          show_areas: false,
          show_map: false,
          map_lines: 'area'
        });
      }
    })();
    return () => { stale = true; };
  }, [isOpen, quote, prefill]);

  // The client's other jobs, for "Move to job…" on an existing quote.
  useEffect(() => {
    if (!isOpen || !current?.id) { setJobOptions([]); return; }
    jobsService.getAll(form.customer_id ? { customerId: form.customer_id } : {}).then((r) => { if (r.success) setJobOptions(r.jobs || []); });
  }, [isOpen, current?.id, form.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function moveToJob(jobId) {
    if (!jobId || !current?.id) return;
    setBusy('move');
    const r = await jobsService.attachQuote(Number(jobId), current.id);
    setBusy('');
    if (r.success) {
      const q = await quotesService.getById(current.id);
      if (q.success) hydrate(q.quote, q.accept_url);
      onSave?.();
    } else {
      setErrors({ submit: r.error || 'Could not move the quote' });
    }
  }

  function hydrate(q, url) {
    setCurrent(q);
    if (url) setAcceptUrl(url);
    setForm({
      customer_id: q.customer_id ? String(q.customer_id) : '',
      appointment_id: q.appointment_id || null,
      job_id: q.job_id || null,
      title: q.title || '',
      intro: q.intro || '',
      terms: q.terms || '',
      includes_gst: !!q.includes_gst,
      valid_until: q.valid_until || '',
      items: (q.items || []).map((it) => newItem({
        service_id: it.service_id ? String(it.service_id) : '', description: it.description, qty: it.qty, unit_price: it.unit_price,
        source: it.source || null, unit: it.unit || null
      })),
      measurements: q.measurements || [],
      show_areas: !!q.show_areas,
      show_map: !!q.show_map,
      map_lines: q.map_lines || 'area'
    });
  }

  // Rebuild the map-sourced lines from the saved shapes for a new mode; hand-typed lines stay.
  function changeMapLines(mode) {
    const manual = form.items.filter((it) => it.source !== 'map' && (it.description.trim() || it.unit_price !== ''));
    let mapItems = [];
    if (mode !== 'none' && form.measurements.length) {
      const built = buildQuoteLines(measurementsToShapes(form.measurements, surfaces), surfaces, settings, { groupBySurface: mode === 'surface', priceLines: true });
      mapItems = built.items.map((it) => newItem(it));
    }
    const patch = { map_lines: mode, items: [...mapItems, ...manual] };
    if (mode === 'none') { patch.show_areas = true; if (!patch.items.length) patch.items = [newItem()]; }
    set(patch);
    flash(mode === 'none' ? 'Priced lines removed — the areas stay listed under "Areas measured". Save to keep this.' : 'Priced lines rebuilt from the measured areas — save to keep this.');
  }

  // The estimator hands back priced lines + the shapes behind them. Map lines
  // replace the previous map lines; anything typed by hand is kept. "Areas
  // only" saves the shapes with no lines and switches the areas list on so
  // the customer still sees what was measured.
  function applyEstimate(result) {
    const manual = form.items.filter((it) => it.source !== 'map' && (it.description.trim() || it.unit_price !== ''));
    const patch = { items: [...result.items.map((it) => newItem(it)), ...manual], measurements: result.measurements, map_lines: !result.priced ? 'none' : result.grouped ? 'surface' : 'area' };
    if (!result.priced) { patch.show_areas = true; if (!manual.length) patch.items = [newItem()]; }
    set(patch);
    setEstimator(null);
    const n = result.measurements.length;
    flash(result.priced
      ? `${n} area${n === 1 ? '' : 's'} applied from the map${result.topUp ? ' (plus the minimum-charge top-up)' : ''} — save to keep them`
      : `${n} area${n === 1 ? '' : 's'} saved without prices — they will be listed on the quote; add your own lines, then save`);
  }

  const locked = current?.status === 'accepted';
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const setItem = (key, patch) => set({ items: form.items.map((it) => (it.key === key ? { ...it, ...patch } : it)) });
  const removeItem = (key) => set({ items: form.items.filter((it) => it.key !== key) });
  const addItem = (patch) => set({ items: [...form.items, newItem(patch)] });
  function addFromService(id) {
    const svc = services.find((s) => String(s.id) === String(id));
    if (!svc) return;
    const priced = svc.price_type !== 'quote' && svc.price !== null && svc.price !== undefined && svc.price !== '';
    addItem({ service_id: String(svc.id), description: svc.name, unit_price: priced ? svc.price : '' });
  }

  const totals = totalsOf(form.items, form.includes_gst);
  const selectedClient = clients.find((c) => String(c.id) === form.customer_id);

  function payload(f = form) {
    return {
      customer_id: f.customer_id ? Number(f.customer_id) : null,
      appointment_id: f.appointment_id || null,
      job_id: f.job_id || null,
      title: f.title.trim(),
      intro: f.intro,
      terms: f.terms,
      includes_gst: f.includes_gst,
      valid_until: f.valid_until || null,
      items: f.items.filter((it) => it.description.trim()).map((it) => ({
        service_id: it.service_id ? Number(it.service_id) : null,
        description: it.description.trim(),
        qty: Number(it.qty) || 1,
        unit_price: Number(it.unit_price) || 0,
        source: it.source || null,
        unit: it.unit || null
      })),
      measurements: f.measurements || [],
      show_areas: !!f.show_areas,
      show_map: !!f.show_map,
      map_lines: f.map_lines || 'area'
    };
  }

  function flash(msg) { setNotice(msg); setTimeout(() => setNotice(''), 3500); }

  // Create or update. Returns the saved quote, or null on failure.
  async function persist(f = form) {
    if (!f.customer_id) { setErrors({ customer_id: 'Choose a client' }); return null; }
    setErrors({});
    const r = current ? await quotesService.update(current.id, payload(f)) : await quotesService.create(payload(f));
    if (!r.success) { setErrors({ submit: r.error || 'Failed to save quote' }); return null; }
    // The measured areas are now on the quote — the estimator's autosave has done its job.
    clearDraftsFor({ quoteId: r.quote.id, customerId: f.customer_id });
    hydrate(r.quote, r.accept_url);
    onSave?.(r.quote);
    return r.quote;
  }
  async function save() {
    setBusy('save');
    const q = await persist();
    setBusy('');
    if (q) flash('Saved');
  }
  // Move a customer line to the internal costings (#773): it becomes a work line and leaves the
  // form, then the whole form is saved through the normal path so both sides stay consistent.
  // Any other edits in the form are saved with it (nothing is lost); if the save fails the work
  // line is removed again and the form is left exactly as it was.
  async function moveToCostings(item) {
    if (!current || !item.description.trim()) return;
    setBusy('move-costing');
    const ws = await worksheetService.get(current.id);
    const cat = ws.success ? guessCategory(item.description, ws.categories) : null;
    if (!cat) { setBusy(''); setErrors({ submit: ws.error || 'No cost category to move this to — add one under Settings' }); return; }
    const made = await worksheetService.addLine(current.id, {
      category_id: cat.id, description: item.description.trim(), qty: Number(item.qty) || 1, unit_cost: Number(item.unit_price) || 0
    });
    if (!made.success) { setBusy(''); setErrors({ submit: made.error || 'Could not move the line' }); return; }
    const saved = await persist({ ...form, items: form.items.filter((it) => it.key !== item.key) });
    setBusy('');
    if (saved) flash('Moved to costings');
    else await worksheetService.deleteLine(made.line.id);
  }
  async function send() {
    if (!selectedClient?.email) { setErrors({ submit: 'This client has no email address. Open the PDF to print it, or add an email under Edit.' }); return; }
    if (!form.items.some((it) => it.description.trim())) { setErrors({ submit: 'Add at least one line item before sending.' }); return; }
    if (!window.confirm(`Email this quote to ${selectedClient.name} (${selectedClient.email}) with the PDF attached?`)) return;
    setBusy('send');
    const saved = await persist();
    if (!saved) { setBusy(''); return; }
    const r = await quotesService.send(saved.id);
    setBusy('');
    if (r.success) {
      hydrate(r.quote, r.accept_url);
      onSave?.(r.quote);
      flash(r.preview ? 'Sent (preview mode — the email was captured, see the server log)' : r.skipped ? 'Email is off on this server — marked as sent anyway' : 'Sent to client');
    } else {
      setErrors({ submit: r.error || 'Could not send' });
    }
  }
  async function setStatus(status) {
    setBusy('status');
    const r = await quotesService.setStatus(current.id, status);
    setBusy('');
    if (r.success) { hydrate(r.quote, r.accept_url); onSave?.(r.quote); flash(status === 'sent' ? 'Marked as given to the client' : `Marked ${STATUS_LABEL[status].toLowerCase()}`); }
    else setErrors({ submit: r.error || 'Could not update status' });
  }
  // The work is already on the job: say which visit books this quote (Bug #394).
  async function linkVisit(appointmentId) {
    if (!appointmentId) return;
    setBusy('link');
    const r = await quotesService.link(current.id, Number(appointmentId));
    setBusy('');
    if (r.success) { hydrate(r.quote, r.accept_url); onSave?.(r.quote); flash('Booked on that visit'); }
    else setErrors({ submit: r.error || 'Could not link the visit' });
  }
  async function remove() {
    if (!window.confirm(`Delete quote ${current.quote_number}? This cannot be undone.`)) return;
    setBusy('delete');
    const r = await quotesService.delete(current.id);
    setBusy('');
    if (r.success) { clearDraftsFor({ quoteId: current.id }); onSave?.(null); onClose(); }
    else setErrors({ submit: r.error || 'Could not delete' });
  }
  function onClientSaved(client) {
    setClients((prev) => (prev.some((c) => c.id === client.id) ? prev.map((c) => (c.id === client.id ? client : c)) : [...prev, client]));
    set({ customer_id: String(client.id) });
    setClientModal(null);
  }

  const status = current?.effective_status || current?.status;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={current ? `Quote ${current.quote_number}${current.job?.job_number ? ` · ${current.job.job_number}` : ''}` : 'New Quote'} size="full">
      <div className="quote-form">
        {current && (
          <div className="quote-toolbar">
            <span className={`status-badge status-${status}`}>{STATUS_LABEL[status] || status}</span>
            {current.sent_at && <span className="text-muted quote-meta">Given to client {formatApptDateLong(current.sent_at)}</span>}
            {current.accepted_at && <span className="text-muted quote-meta">Accepted {formatApptDateLong(current.accepted_at)}{current.accepted_name ? ` by ${current.accepted_name}` : ''}</span>}
            {current.booked_appointment && (
              <span className="text-muted quote-meta">Job booked {formatApptDateLong(current.booked_appointment.starts_at)} {formatApptTime(current.booked_appointment.starts_at)}</span>
            )}
            <span className="quote-toolbar-spacer" />
            <a className="btn btn-sm btn-primary" href={quotesService.pdfUrl(current.id)} target="_blank" rel="noopener noreferrer"
              title="Opens the PDF — print it or save it from there">Open PDF</a>
            {form.measurements.length > 0 && (
              <button type="button" className="btn btn-sm btn-outline" onClick={() => setEstimator(locked ? 'view' : 'edit')}
                title={locked ? 'See the areas marked on the satellite map' : 'Edit the areas marked on the satellite map'}>
                Map areas ({form.measurements.length})
              </button>
            )}
            {!locked && (
              <button type="button" className="btn btn-sm btn-outline" onClick={send} disabled={!!busy || !selectedClient?.email}
                title={selectedClient?.email ? `Email the PDF to ${selectedClient.email}` : 'No email on file for this client'}>
                {busy === 'send' ? 'Sending…' : current.status === 'sent' ? 'Email again' : 'Email to client'}
              </button>
            )}
            {!locked && current.status === 'draft' && (
              <button type="button" className="btn btn-sm btn-outline" onClick={() => setStatus('sent')} disabled={!!busy}
                title="Printed, handed over or texted — start the validity clock without emailing">Mark as given</button>
            )}
            {!locked && current.status !== 'declined' && <button type="button" className="btn btn-sm btn-outline" onClick={() => setStatus('accepted')} disabled={!!busy}>Mark accepted</button>}
            {!locked && current.status !== 'declined' && <button type="button" className="btn btn-sm btn-outline" onClick={() => setStatus('declined')} disabled={!!busy}>Mark declined</button>}
            {(locked || current.status === 'declined') && <button type="button" className="btn btn-sm btn-outline" onClick={() => setStatus('draft')} disabled={!!busy}>Reopen as draft</button>}
            {!current.booked_appointment && current.job_visits?.length > 0 && (
              <select aria-label="Booked on which visit" className="quote-job-move" value="" onChange={(e) => linkVisit(e.target.value)} disabled={!!busy}
                title="The work is already on the job — pick the visit that books this quote">
                <option value="">Booked on visit…</option>
                {current.job_visits.map((v) => <option key={v.id} value={v.id}>{formatApptDateLong(v.starts_at)} {formatApptTime(v.starts_at)} · {v.service_name || 'Visit'}</option>)}
              </select>
            )}
            {onBook && !current.booked_appointment && <button type="button" className="btn btn-sm btn-primary" onClick={() => onBook(current)} disabled={!!busy}>{current.job_visits?.length ? 'Book another visit' : 'Book job'}</button>}
            <button type="button" className="btn btn-sm btn-danger" onClick={remove} disabled={!!busy}>Delete</button>
          </div>
        )}
        {locked && <p className="quote-locked text-muted">This quote has been accepted and is locked. Reopen it as a draft to make changes.</p>}
        {!current && <p className="quote-locked text-muted">Save the draft first — then you can open the PDF to print or save it, email it, or mark it as given.</p>}
        {acceptUrl && current && !locked && (
          <p className="quote-link text-muted">Client link: <a href={acceptUrl} target="_blank" rel="noopener noreferrer">{acceptUrl}</a></p>
        )}

        {current?.job && (
          <p className="quote-job-row text-muted">
            On job <Link to={`/jobs/${current.job.id}`} onClick={onClose}><strong>{current.job.job_number}</strong></Link>{current.job.title ? ` · ${current.job.title}` : ''}
            {jobOptions.some((j) => j.id !== current.job.id) && (
              <select aria-label="Move to job" className="quote-job-move" value="" onChange={(e) => moveToJob(e.target.value)} disabled={!!busy} title="Move this quote (and the visits it came from or booked) onto another job">
                <option value="">Move to job…</option>
                {jobOptions.filter((j) => j.id !== current.job.id).map((j) => <option key={j.id} value={j.id}>{j.job_number}{j.title ? ` · ${j.title}` : ''}{j.customer_name ? ` · ${j.customer_name}` : ''}</option>)}
              </select>
            )}
          </p>
        )}

        <div className="quote-tabs" role="tablist">
          {[['quote', 'Quote'], ['costings', 'Costings · internal'], ['materials', 'Materials list']].map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} className={`quote-tab ${tab === key ? 'active' : ''}`}
              onClick={() => setTab(key)} disabled={key !== 'quote' && !current} title={key !== 'quote' && !current ? 'Save the quote first' : undefined}>{label}</button>
          ))}
        </div>
        {current && tab === 'costings' && <QuoteWorksheet quoteId={current.id} />}
        {current && tab === 'materials' && <MaterialsList quoteId={current.id} />}

        <fieldset disabled={locked || busy === 'delete'} className="quote-fields" hidden={tab !== 'quote'}>
          <div className="form-group">
            <label htmlFor="q_customer">Client *</label>
            <div className="inline-select-row">
              <select id="q_customer" value={form.customer_id} onChange={(e) => set({ customer_id: e.target.value })}>
                <option value="">Select…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` — ${c.email}` : ''}</option>)}
              </select>
              {selectedClient && <button type="button" className="btn btn-outline" onClick={() => setClientModal({ client: selectedClient })}>Edit</button>}
              <button type="button" className="btn btn-outline" onClick={() => setClientModal({ client: null })}>+ New</button>
            </div>
            {errors.customer_id && <span className="form-error">{errors.customer_id}</span>}
            {selectedClient && !selectedClient.email && (
              <span className="text-muted quote-hint">No email on file — open the PDF to print or save it, then use "Mark as given". Add an email under Edit if you'd rather send it.</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="q_title">Job title</label>
              <input id="q_title" value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. House soft wash + driveway" />
            </div>
            <div className="form-group">
              <label htmlFor="q_valid">Valid until</label>
              <input id="q_valid" type="date" value={form.valid_until} onChange={(e) => set({ valid_until: e.target.value })} />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="q_intro">Note to client (optional)</label>
            <textarea id="q_intro" rows="2" value={form.intro} onChange={(e) => set({ intro: e.target.value })} placeholder="Thanks for having us out — here's the price for…" />
          </div>

          <div className="form-group">
            <div className="quote-items-head">
              <label>Itemised breakdown</label>
              <div className="quote-items-actions">
                <button type="button" className="btn btn-sm btn-primary" onClick={() => setEstimator('edit')} disabled={!selectedClient}
                  title={selectedClient ? 'Mark out the surfaces on the satellite map and price them by the square metre' : 'Choose a client first'}>
                  {form.measurements.length ? `Edit map areas (${form.measurements.length})` : 'Measure from map'}
                </button>
                <select value="" onChange={(e) => addFromService(e.target.value)} aria-label="Add a line from a job type">
                  <option value="">+ From job type…</option>
                  {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button type="button" className="btn btn-sm btn-outline" onClick={() => addItem()}>+ Add line</button>
              </div>
            </div>
            {form.items.length === 0 ? (
              <div className="quote-empty">
                <p className="text-muted">List each part of the job with its price, e.g. "Pressure wash pool area" $300.</p>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => addItem()}>+ Add a line</button>
              </div>
            ) : (
              <div className="quote-items">
                <div className="quote-item quote-item-header">
                  <span>Description</span><span>Qty</span><span>Unit{form.includes_gst ? ' (inc GST)' : ''}</span><span>Amount</span><span />
                </div>
                {form.items.map((it) => (
                  <div key={it.key} className={`quote-item ${it.source === 'map' ? 'from-map' : ''}`}
                    title={it.source === 'map' ? 'Measured on the map — reopen the map to re-measure, or edit the figures here' : undefined}>
                    <input aria-label="Description" value={it.description} onChange={(e) => setItem(it.key, { description: e.target.value })} placeholder="e.g. Pressure wash pool area" autoFocus={it.key === form.items[form.items.length - 1].key && !it.description} />
                    <input aria-label={it.unit === 'm2' ? 'Square metres' : it.unit === 'lm' ? 'Lineal metres' : 'Qty'} type="number" min="0" step={it.unit ? '0.1' : '0.5'} value={it.qty} onChange={(e) => setItem(it.key, { qty: e.target.value })} />
                    <input aria-label="Unit price" type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(it.key, { unit_price: e.target.value })} placeholder="0.00" />
                    <span className="quote-item-total">{formatMoney(round2((Number(it.qty) || 0) * (Number(it.unit_price) || 0)))}</span>
                    <span className="quote-item-actions">
                      {current && !locked && it.description.trim() && (
                        <button type="button" className="quote-item-move" aria-label="Move to costings" title="Move to costings (internal)" disabled={!!busy} onClick={() => moveToCostings(it)}>⇢</button>
                      )}
                      <button type="button" className="quote-item-remove" aria-label="Remove line" onClick={() => removeItem(it.key)}>×</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {form.measurements.length > 0 && (
            <div className="quote-areas-options">
              <label className="quote-map-lines" htmlFor="q_map_lines" title="How the measured areas land on this quote. Switching rebuilds the map lines from the saved shapes; lines you typed yourself are kept.">
                <span>Measured areas on the quote</span>
                <select id="q_map_lines" value={form.map_lines || 'area'} onChange={(e) => changeMapLines(e.target.value)}>
                  {MAP_LINE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label className="checkbox-label" title="A table of the measured areas — label, surface, size — with no prices. Fine alongside a lump-sum line.">
                <input type="checkbox" checked={!!form.show_areas} onChange={(e) => set({ show_areas: e.target.checked })} /> List the measured areas on the quote
              </label>
              <label className="checkbox-label" title="Adds the marked-up satellite view to the PDF and the client's quote page">
                <input type="checkbox" checked={!!form.show_map} onChange={(e) => set({ show_map: e.target.checked })} /> Include the marked-up map on the PDF
              </label>
              {form.show_map && (current
                ? <MapPreview id={current.id} version={`${current.updated_at}-${form.measurements.length}`} />
                : <span className="text-muted quote-hint">Save the draft to preview the map.</span>)}
            </div>
          )}

          <div className="quote-totals-row">
            <label className="checkbox-label">
              <input type="checkbox" checked={form.includes_gst} onChange={(e) => set({ includes_gst: e.target.checked })} /> Prices include GST
            </label>
            <div className="quote-totals">
              {form.includes_gst && (
                <>
                  <span>Subtotal (ex GST)</span><span>{formatMoney(totals.subtotal)}</span>
                  <span>GST (10%)</span><span>{formatMoney(totals.gst)}</span>
                </>
              )}
              <strong>Total{form.includes_gst ? ' (inc GST)' : ''}</strong><strong>{formatMoney(totals.total)}</strong>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="q_terms">Terms</label>
            <textarea id="q_terms" rows="3" value={form.terms} onChange={(e) => set({ terms: e.target.value })} />
          </div>
        </fieldset>

        {errors.submit && <div className="form-error text-center mb-md">{errors.submit}</div>}
        {notice && <div className="text-success text-center mb-md">{notice}</div>}

        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
          {!locked && (
            <button type="button" className="btn btn-primary" onClick={save} disabled={!!busy}>
              {busy === 'save' ? 'Saving…' : current ? 'Save changes' : 'Save draft'}
            </button>
          )}
        </div>
      </div>

      <ClientModal isOpen={!!clientModal} onClose={() => setClientModal(null)} onSave={onClientSaved} client={clientModal?.client || null} />
      <EstimatorModal
        isOpen={!!estimator}
        onClose={() => setEstimator(null)}
        client={selectedClient || null}
        surfaces={surfaces}
        settings={settings}
        initial={form.measurements}
        readOnly={estimator === 'view' || locked}
        onApply={applyEstimate}
        draftId={{ quoteId: current?.id || null, customerId: form.customer_id ? Number(form.customer_id) : null }}
        savedAt={current?.updated_at || null}
        priceLinesDefault={form.map_lines !== 'none'}
        groupDefault={form.map_lines === 'surface'}
      />
    </Modal>
  );
}
